import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, LockKeyhole, Mail } from 'lucide-react';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import { adminAuthService } from '../../../services/admin/adminAuthService';

const LaborOperationsLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAdminAuth();
  const [form, setForm] = useState({ email: '', password: '', remember: false });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await adminAuthService.login(form.email, form.password, form.remember);
      login(response.user, response.token);
      navigate(location.state?.from || '/admin/labor-intake', { replace: true });
    } catch (loginError) {
      setError(loginError.message || 'تعذر تسجيل الدخول.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="labor-operations relative flex min-h-screen overflow-hidden bg-[#f6f4ef]" dir="rtl">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_28%_20%,rgba(162,140,107,0.09),transparent_31%),radial-gradient(circle_at_80%_90%,rgba(181,166,142,0.07),transparent_29%)]" />
      <section className="relative hidden flex-1 flex-col justify-between bg-[linear-gradient(145deg,#fbf9f4_0%,#f0ede6_100%)] p-14 lg:flex">
        <div>
          <div className="inline-flex px-2 py-2">
            <img src="/images/wazin-logo.png" alt="Wazin" className="h-24 w-56 object-contain object-right mix-blend-multiply" />
          </div>
          <p className="lo-kicker mt-5 font-bold">WAZIN LEGAL OPERATIONS</p>
        </div>
        <div className="max-w-xl p-5">
          <p className="lo-kicker font-bold">CASE MANAGEMENT WORKSPACE</p>
          <h1 className="legal-title mt-5 text-[43px] font-bold leading-[1.48] text-[#273950]">غرفة عمليات قانونية لإدارة ملفات القضايا العمالية.</h1>
          <p className="mt-5 text-base leading-8 text-[#687384]">مراجعة الطلبات، تنظيم المستندات، متابعة النواقص، وتحويل بيانات العميل إلى ملف عمل قانوني منظم.</p>
          <div className="relative mt-10 rounded-[18px] bg-[#eeebe4]/72 p-4">
            <span className="legal-motion-glow pointer-events-none absolute inset-x-10 top-10 h-28 rounded-full bg-[#d6bb89]/16 blur-3xl" />
            <div className="relative grid grid-cols-3 gap-2.5">
              {[
                ['طلبات جديدة', '08'],
                ['نواقص قيد المتابعة', '03'],
                ['ملفات مسندة', '12'],
              ].map(([title, value]) => (
                <div key={title} className="legal-motion-card rounded-xl bg-[#fffdfa]/72 p-4">
                  <p className="text-[12px] leading-6 text-[#697483]">{title}</p>
                  <p className="mt-2 text-xl font-bold text-[#273950]">{value}</p>
                </div>
              ))}
            </div>
            <div className="relative mt-5 flex items-center justify-between rounded-xl bg-[#fffdfa]/55 px-4 py-4">
              {['استلام', 'تحليل داخلي', 'مراجعة قانونية', 'إسناد'].map((stage, index) => (
                <React.Fragment key={stage}>
                  <div className="flex flex-col items-center gap-2">
                    <span className={`legal-motion-pulse h-2.5 w-2.5 rounded-full ${index < 3 ? 'bg-[#948066]' : 'bg-[#ded3c0]'}`} style={{ animationDelay: `${index * 0.42}s` }} />
                    <span className="text-[12px] text-[#697483]">{stage}</span>
                  </div>
                  {index < 3 && <span className="mb-5 h-px flex-1 bg-[linear-gradient(90deg,#eadfcf,#c9ad79,#eadfcf)]" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
        <p className="text-xs text-[#98896f]">منصة داخلية خاصة بفريق وازن القانوني</p>
      </section>

      <section className="relative flex w-full items-center justify-center px-6 lg:w-[500px] lg:bg-[#fcfaf6]/75">
        <form onSubmit={submit} className="lo-surface w-full max-w-[390px] rounded-[20px] p-9 backdrop-blur">
          <div className="mb-10 lg:hidden">
            <img src="/images/wazin-logo.png" alt="Wazin" className="h-16 w-40 object-contain object-right mix-blend-multiply" />
            <p className="lo-gold mt-3 text-[13px] tracking-widest">LEGAL OPERATIONS</p>
          </div>
          <p className="lo-gold text-[13px] font-bold tracking-wider">دخول الفريق القانوني</p>
          <h2 className="legal-title mt-3 text-[29px] font-bold text-[#273950]">تسجيل الدخول</h2>
          <p className="mt-2 text-sm text-[#7a8491]">الوصول إلى مركز إدارة القضايا العمالية.</p>

          {error && <div className="mt-7 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

          <label className="mt-8 block text-sm font-bold text-[#273950]">
            البريد الإلكتروني
            <span className="mt-2 flex items-center gap-2 rounded-xl border border-[#e7decf] bg-[#fdfcf9] px-3 transition focus-within:border-[#c3a168] focus-within:shadow-[0_0_0_3px_rgba(195,161,104,0.1)]">
              <Mail className="h-4 w-4 text-[#ae9d81]" />
              <input type="email" required value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} className="h-12 flex-1 bg-transparent text-left text-sm outline-none" dir="ltr" />
            </span>
          </label>
          <label className="mt-5 block text-sm font-bold text-[#273950]">
            كلمة المرور
            <span className="mt-2 flex items-center gap-2 rounded-xl border border-[#e7decf] bg-[#fdfcf9] px-3 transition focus-within:border-[#c3a168] focus-within:shadow-[0_0_0_3px_rgba(195,161,104,0.1)]">
              <LockKeyhole className="h-4 w-4 text-[#ae9d81]" />
              <input type="password" required value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} className="h-12 flex-1 bg-transparent text-left text-sm outline-none" dir="ltr" />
            </span>
          </label>
          <label className="mt-5 flex items-center gap-2 text-sm text-[#7a8491]">
            <input type="checkbox" checked={form.remember} onChange={(event) => setForm((current) => ({ ...current, remember: event.target.checked }))} className="accent-[#948066]" />
            إبقائي مسجلاً لمدة 7 أيام
          </label>
          <button type="submit" disabled={loading} className="mt-8 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#33455d] text-sm font-bold text-white shadow-[0_10px_24px_rgba(39,57,80,0.16)] transition hover:bg-[#293a52] disabled:opacity-50">
            {loading ? 'جاري الدخول...' : 'الدخول إلى المنصة'}
            {!loading && <ArrowLeft className="h-4 w-4" />}
          </button>
        </form>
      </section>
    </main>
  );
};

export default LaborOperationsLoginPage;
